import { AxiosResponse } from 'axios';
import { axiosRequest } from '@/common/api/instance';
import { User, UserOrganisation } from '@/types/api/auth.types';
import {
  MetaWithoutPagination,
  PaginatedResponse,
  Response as ApiResponse,
} from '@/types/api/response.types';
import {
  AccountPayable,
  AccountsParams,
  AccountsPayableTotals,
  CreateOrgBody,
  CreateOrgOrder,
  DeleteDocsBody,
  OrderVendor,
  Organisation,
  OrganisationProjectListForUser,
  OrgDocsBody,
  OrgSite,
  OrgSiteParams,
  OrgUser,
  PaymentRequest,
  PaymentRequestTotals,
  PRItem,
  ProjectParams,
  UserRole,
  UsersByRoleCodeResponse,
} from '@/types/api/organisations.types';
import { Project } from '@/types/api/project.types';
import { BOQ } from '@/types/api/sites.types';
import { Template } from '@/types/api/boqs.types';
import { GetOrdersParams, OrderClaim, OrderListItem } from '@/types/api/orders.types';
import { BOQRoles, OrgRoles, ProjRoles, SiteRoles } from '@/types/api/roles.types';

const resource = '/organisations';

export const registerOrganisation = async (
  data: CreateOrgBody,
): Promise<AxiosResponse<ApiResponse<Organisation>>> => {
  return axiosRequest().post(`${resource}/register`, data);
};

export const getOrgUsersByRoleCode = async (
  orgId: string,
  roles: (BOQRoles | OrgRoles | ProjRoles | SiteRoles)[],
  siteIds?: string[],
): Promise<AxiosResponse<ApiResponse<UsersByRoleCodeResponse[]>>> => {
  const params = new URLSearchParams();

  if (roles && Array.isArray(roles)) {
    for (const role of roles) {
      params.append('roles', role);
    }
  }

  if (siteIds && Array.isArray(siteIds)) {
    for (const siteId of siteIds) {
      params.append('siteIds', siteId);
    }
  }

  const url = `${resource}/${orgId}/users/summary?${params.toString()}`;

  return axiosRequest().get(url);
};

export const uploadDocuments = async (
  data: OrgDocsBody,
): Promise<AxiosResponse<ApiResponse<Organisation>>> => {
  return axiosRequest().patch(`${resource}/${data.id}/documents`, data.body);
};

export const deleteDocuments = async (
  data: DeleteDocsBody,
): Promise<AxiosResponse<ApiResponse<MetaWithoutPagination>>> => {
  return axiosRequest().delete(`${resource}/${data.id}/documents`, { data });
};

export const deleteUser = async (
  orgId: string,
  userId: string,
): Promise<AxiosResponse<ApiResponse<MetaWithoutPagination>>> => {
  return axiosRequest().delete(`${resource}/${orgId}/users/${userId}`);
};

export const getOrganisationDetails = async (
  id: string,
): Promise<AxiosResponse<ApiResponse<Organisation>>> => {
  return axiosRequest().get(`${resource}/${id}`);
};

export const getCurrentUserOrgs = async (): Promise<
  AxiosResponse<ApiResponse<UserOrganisation[]>>
> => {
  return axiosRequest().get(`${resource}`);
};

export const setOrganisationPrimary = async (
  id: string,
): Promise<AxiosResponse<ApiResponse<UserOrganisation[]>>> => {
  return axiosRequest().patch(`${resource}/${id}/primary`);
};

export const getCurrentOrgUsers = async (
  id: string,
): Promise<AxiosResponse<ApiResponse<OrgUser[]>>> => {
  return axiosRequest().get(`${resource}/${id}/users`);
};

export const getOrgProjectListByUser = async (
  id: string,
  userId: string,
): Promise<AxiosResponse<ApiResponse<OrganisationProjectListForUser>>> => {
  return axiosRequest().get(`${resource}/${id}/users/${userId}/roles`);
};

export const updateUserRole = async (
  orgId: string,
  userId: string,
  role: UserRole,
): Promise<AxiosResponse<ApiResponse<OrgUser>>> => {
  return axiosRequest().patch(`${resource}/${orgId}/users/${userId}`, { orgUserRole: role });
};

export const addOrgUserRole = async (
  orgId: string,
  userId: string,
  roleCode: OrgRoles,
): Promise<AxiosResponse<ApiResponse<OrgUser>>> => {
  return axiosRequest().post(`${resource}/${orgId}/users/${userId}/roles`, { roleCode });
};

export const removeOrgUserRole = async (
  orgId: string,
  userId: string,
  roleCode: OrgRoles,
): Promise<AxiosResponse<ApiResponse<OrgUser>>> => {
  return axiosRequest().delete(`${resource}/${orgId}/users/${userId}/roles/${roleCode}`);
};

export const updateOrganisationDetails = async (
  id: string,
  data: Partial<Organisation>,
): Promise<AxiosResponse<ApiResponse<Organisation>>> => {
  return axiosRequest().put(`${resource}/${id}`, data);
};

export const getOrganisationProjects = async (
  orgId: string,
  params: ProjectParams,
): Promise<AxiosResponse<PaginatedResponse<Project[]>>> => {
  return axiosRequest().get(`${resource}/${orgId}/projects`, { params });
};

export const getProjectManagers = async (
  id: string,
): Promise<AxiosResponse<ApiResponse<User[]>>> => {
  return axiosRequest().get(`${resource}/${id}/project-managers`);
};

export const getBoqTemplates = async (
  id: string,
): Promise<AxiosResponse<ApiResponse<Template[]>>> => {
  return axiosRequest().get(`${resource}/${id}/boqs/templates`);
};

export const updateTemplate = (
  id: string,
  templateId: string,
  isPublic: boolean,
): Promise<AxiosResponse<ApiResponse<BOQ>>> => {
  return axiosRequest().put(`${resource}/${id}/boqs/templates/${templateId}`, { public: isPublic });
};

export const getPaymentRequestVendors = async (
  id: string,
): Promise<AxiosResponse<ApiResponse<PRItem[]>>> => {
  return axiosRequest().get(`${resource}/${id}/payment-requests/vendors`);
};

export const getPaymentRequestSites = async (
  id: string,
): Promise<AxiosResponse<ApiResponse<PRItem[]>>> => {
  return axiosRequest().get(`${resource}/${id}/payment-requests/sites`);
};

export const getPaymentRequestsCount = async (
  id: string,
): Promise<AxiosResponse<ApiResponse<number>>> => {
  return axiosRequest().get(`${resource}/${id}/payment-requests/count`);
};

export const getPaymentRequestTotals = async (params: {
  id: string;
  vendorId?: string;
}): Promise<AxiosResponse<ApiResponse<PaymentRequestTotals>>> => {
  return axiosRequest().get(`${resource}/${params.id}/payment-requests/totals`, { params: params });
};

export const getPaymentRequests = async (
  orgId: string,
  params?: AccountsParams,
): Promise<AxiosResponse<PaginatedResponse<PaymentRequest[]>>> => {
  return axiosRequest().get(`${resource}/${orgId}/payment-requests`, { params: params });
};

export const getAccountsPayableTotals = async (params: {
  id: string;
  accountId?: string;
}): Promise<AxiosResponse<ApiResponse<AccountsPayableTotals>>> => {
  return axiosRequest().get(`${resource}/${params.id}/accounts-payable/totals`, { params: params });
};

export const getAccountsPayable = async (
  orgId: string,
  params?: AccountsParams,
): Promise<AxiosResponse<PaginatedResponse<AccountPayable[]>>> => {
  return axiosRequest().get(`${resource}/${orgId}/accounts-payable`, { params: params });
};

export const getOrders = async (
  id: string,
  params: GetOrdersParams,
): Promise<AxiosResponse<PaginatedResponse<OrderListItem[]>>> => {
  return axiosRequest().get(`${resource}/${id}/orders`, { params: params });
};

export const getOrderVendors = async (
  id: string,
): Promise<AxiosResponse<ApiResponse<OrderVendor[]>>> => {
  return axiosRequest().get(`${resource}/${id}/orders/vendors`);
};

export const uploadOrgLogo = async (
  data: OrgDocsBody,
): Promise<AxiosResponse<ApiResponse<Organisation>>> => {
  return axiosRequest().post(`${resource}/${data.id}/logos`, data.body);
};

export const deleteOrgLogo = async (
  id: string,
): Promise<AxiosResponse<ApiResponse<MetaWithoutPagination>>> => {
  return axiosRequest().delete(`${resource}/${id}/logos`);
};

export const getOrgSites = async (
  id: string,
  params: OrgSiteParams,
): Promise<AxiosResponse<PaginatedResponse<OrgSite[]>>> => {
  return axiosRequest().get(`${resource}/${id}/sites/summary`, { params: params });
};

export const createOrgOrder = async (
  id: string,
  data: CreateOrgOrder,
): Promise<AxiosResponse<ApiResponse<OrderClaim[]>>> => {
  return axiosRequest().post(`${resource}/${id}/orders`, data);
};

export const createOrgClaim = async (
  id: string,
  data: CreateOrgOrder,
): Promise<AxiosResponse<ApiResponse<OrderClaim[]>>> => {
  return axiosRequest().post(`${resource}/${id}/claims`, data);
};
